/**
 * ログイン状態の延長・再ログイン・ログアウト（03 3.3.3・3.3.4・3.4.5）。
 * - 反応型：API の 401 → API-AUTH-03 を 1 回だけ（同時に来た 401 はこの 1 回を待つ）→ だめなら ReloginDialog
 * - 先回り：access_token_expires_at の 60 秒前に、直近 15 分に操作があれば API-AUTH-03（FE-D-027）
 * - ログアウト：API-AUTH-04 → 結果に関わらず queryClient.clear()・BroadcastChannel('pcrm-auth') に logout → /login（FE-D-026）
 * - 403 AUTH_006：ダイアログを出さずに /login へ移り AUTH_006 の文言を出す
 */
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthVerifyResponse, Me } from '@pcrm/shared';
import { diffSecondsUtc, nowUtc } from '@pcrm/shared';
import { registerSessionHandlers } from '../../../api/client';
import { logout as logoutApi, refreshSession } from '../../../api/endpoints/auth';
import { LEAVE_WITHOUT_PROMPT_STATE } from '../../../components/form/FormParts';
import { ME_QUERY_KEY, meOf } from '../queries';
import { ReloginDialog } from './ReloginDialog';

const REFRESH_AHEAD_SECONDS = 60;
const ACTIVITY_WINDOW_MS = 15 * 60 * 1000;
const AUTH_CHANNEL_NAME = 'pcrm-auth';

export type LoginNoticeCode = 'AUTH_LOGOUT' | 'AUTH_006';

export interface AuthSessionValue {
  /** ログインの直後・延長の直後に呼ぶ（先回りの延長の時刻を決める） */
  noteAccessTokenExpiry(access_token_expires_at: string): void;
  logout(): Promise<void>;
}

const auth_session_context = createContext<AuthSessionValue | null>(null);

export function useAuthSession(): AuthSessionValue {
  const value = useContext(auth_session_context);
  if (!value) {
    throw new Error('useAuthSession は AuthSessionProvider の中で使います');
  }
  return value;
}

interface PendingRelogin {
  email: string;
  user_code: string;
  resolve: (is_same_user: boolean) => void;
}

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const query_client = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingRelogin | null>(null);
  const refresh_inflight = useRef<Promise<boolean> | null>(null);
  const relogin_inflight = useRef<Promise<boolean> | null>(null);
  const last_activity_ms = useRef(Date.now());
  const refresh_timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const channel = useRef<BroadcastChannel | null>(null);

  const noteAccessTokenExpiry = useCallback((access_token_expires_at: string) => {
    if (refresh_timer.current) {
      clearTimeout(refresh_timer.current);
    }
    const wait_ms = Math.max(0, (diffSecondsUtc(nowUtc(), access_token_expires_at) - REFRESH_AHEAD_SECONDS) * 1000);
    refresh_timer.current = setTimeout(() => {
      if (Date.now() - last_activity_ms.current <= ACTIVITY_WINDOW_MS) {
        void refreshSession()
          .then((result) => noteAccessTokenExpiry(result.access_token_expires_at))
          .catch(() => undefined);
      }
    }, wait_ms);
  }, []);

  const clearSession = useCallback(
    (notice: LoginNoticeCode) => {
      query_client.clear();
      // 入力中の画面（未保存の確認）があっても止めずに移る（前の利用者の画面を残さない：FE-D-026）
      navigate('/login', { replace: true, state: { notice, ...LEAVE_WITHOUT_PROMPT_STATE } });
    },
    [navigate, query_client],
  );

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // 結果に関わらずログアウトする
    }
    channel.current?.postMessage('logout');
    clearSession('AUTH_LOGOUT');
  }, [clearSession]);

  useEffect(() => {
    const markActivity = () => {
      last_activity_ms.current = Date.now();
    };
    window.addEventListener('click', markActivity);
    window.addEventListener('keydown', markActivity);
    if (typeof BroadcastChannel !== 'undefined') {
      channel.current = new BroadcastChannel(AUTH_CHANNEL_NAME);
      channel.current.onmessage = (event: MessageEvent) => {
        if (event.data === 'logout') {
          clearSession('AUTH_LOGOUT');
        }
      };
    }
    return () => {
      window.removeEventListener('click', markActivity);
      window.removeEventListener('keydown', markActivity);
      channel.current?.close();
      channel.current = null;
      if (refresh_timer.current) {
        clearTimeout(refresh_timer.current);
      }
    };
  }, [clearSession]);

  useEffect(() => {
    registerSessionHandlers({
      refresh: () => {
        last_activity_ms.current = Date.now();
        refresh_inflight.current ??= refreshSession()
          .then((result) => {
            noteAccessTokenExpiry(result.access_token_expires_at);
            return true;
          })
          .catch(() => false)
          .finally(() => {
            refresh_inflight.current = null;
          });
        return refresh_inflight.current;
      },
      reloginRequired: () => {
        const me = query_client.getQueryData<Me>(ME_QUERY_KEY);
        if (!me) {
          // 最初の読み込み（まだログインしていない）は RequireAuth がログイン画面へ移す
          return Promise.resolve(false);
        }
        relogin_inflight.current ??= new Promise<boolean>((resolve) => {
          setPending({ email: me.email, user_code: me.user_code, resolve });
        }).finally(() => {
          relogin_inflight.current = null;
        });
        return relogin_inflight.current;
      },
      accountDisabled: () => clearSession('AUTH_006'),
    });
    return () => registerSessionHandlers(null);
  }, [clearSession, noteAccessTokenExpiry, query_client]);

  const onRelogin = useCallback(
    (result: AuthVerifyResponse) => {
      if (!pending) {
        return;
      }
      const is_same_user = result.user_code === pending.user_code;
      if (!is_same_user) {
        // 前の利用者の画面データ（一覧・詳細のキャッシュ）を残さない。staleTime の間にそのまま出るのを防ぐ（243 UT-640）
        query_client.clear();
      }
      query_client.setQueryData<Me>(ME_QUERY_KEY, meOf(result));
      noteAccessTokenExpiry(result.access_token_expires_at);
      pending.resolve(is_same_user);
      setPending(null);
      if (!is_same_user) {
        // 別の利用者でログインした：止めていた要求を捨てて / へ（FE-D-031）。入力中の画面の「変更を破棄しますか？」で止めない
        navigate('/', { replace: true, state: LEAVE_WITHOUT_PROMPT_STATE });
      }
    },
    [navigate, noteAccessTokenExpiry, pending, query_client],
  );

  const value = useMemo(() => ({ noteAccessTokenExpiry, logout }), [noteAccessTokenExpiry, logout]);

  return (
    <auth_session_context.Provider value={value}>
      {children}
      {pending && <ReloginDialog is_open email={pending.email} onLoggedIn={onRelogin} />}
    </auth_session_context.Provider>
  );
}
